import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Truck, Store } from "lucide-react";
import { api, gbp, imgSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useMeta } from "@/lib/useMeta";
import { StoreBadge } from "@/components/StoreBadge";
import Reviews from "@/components/Reviews";
import { Stars } from "@/components/Stars";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState(false);
  const { add, setOpen } = useCart();
  const meta = useMeta();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [id]);

  useEffect(() => {
    if (!p) return;
    document.title = `${p.name} | Albion Pantry`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", p.description || p.name);
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org/", "@type": "Product", name: p.name, description: p.description,
      image: [imgSrc(p.image_url)], sku: p.id, category: p.category,
      ...(p.rating_count > 0 && { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.rating_count } }),
      offers: {
        "@type": "Offer", url: window.location.href, priceCurrency: "GBP", price: p.price.toFixed(2),
        availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", itemCondition: "https://schema.org/NewCondition",
        shippingDetails: { "@type": "OfferShippingDetails", shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" } },
      },
    });
    document.head.appendChild(el);
    return () => { el.remove(); document.title = "Albion Pantry | Fine British Food"; };
  }, [p]);

  if (err) return <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">Product not found. <Link to="/" className="underline">Back to shop</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#8C2220]" data-testid="back-to-shop"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>
      <div className="mt-8 grid lg:grid-cols-2 gap-12 items-start">
        <div className="rounded-3xl bg-[#F7F3EB] p-10 aspect-square rise">
          <img src={imgSrc(p.image_url)} alt={p.name} className="w-full h-full object-contain" data-testid="product-image" />
        </div>
        <div className="rise" style={{ animationDelay: "120ms" }}>
          <StoreBadge store={p.store} />
          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-[#8C2220]">{p.category}</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold leading-tight" data-testid="product-name">{p.name}</h1>
          {p.rating_count > 0 && <a href="#reviews"><Stars value={p.rating_avg} count={p.rating_count} size="w-4 h-4" className="mt-3" /></a>}
          <p className="mt-5 font-serif text-3xl font-bold" data-testid="product-price">{gbp(p.price)} {p.weight && <span className="font-sans text-sm font-normal text-slate-500">· {p.weight}</span>}</p>
          <p className="mt-6 text-slate-700 leading-relaxed" data-testid="product-description">{p.description}</p>
          {(p.ingredients || p.allergens) && (
            <div className="mt-6 rounded-2xl bg-white border border-[#EDE4D3] divide-y divide-[#EDE4D3]" data-testid="product-info">
              {p.ingredients && (
                <details className="p-5 group" open>
                  <summary className="font-semibold cursor-pointer" data-testid="ingredients-toggle">Ingredients</summary>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="product-ingredients">{p.ingredients}</p>
                </details>
              )}
              {p.allergens && (
                <details className="p-5">
                  <summary className="font-semibold cursor-pointer" data-testid="allergens-toggle">Allergen information</summary>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="product-allergens">{p.allergens}</p>
                </details>
              )}
              <p className="p-5 text-xs text-slate-500">Product information is provided by the retailer. Recipes can change, so always check the pack before eating, especially if you have an allergy.</p>
            </div>
          )}
          {p.in_stock ? (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center rounded-full border border-[#E2D7C3] bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 grid place-items-center" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(p.stock_qty ?? 20, 20, qty + 1))} className="w-11 h-11 grid place-items-center" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={() => { add(p, qty); setOpen(true); }} className="flex-1 rounded-full bg-[#8C2220] text-white py-3.5 font-semibold hover:bg-[#701A18] active:scale-[0.98] transition-[background-color,transform]" data-testid="add-to-basket-button">
                Add to basket
              </button>
            </div>
          ) : (
            <p className="mt-8 rounded-xl bg-rose-50 text-rose-800 p-4 text-sm" data-testid="product-sold-out">Currently sold out at our local stores.</p>
          )}
          <div className="mt-10 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-2xl bg-white border border-[#EDE4D3] p-5"><Store className="w-5 h-5 text-[#8C2220]" /><p className="mt-3 font-semibold">Bought fresh from {p.store}</p><p className="text-slate-500 mt-1">If it's sold out, we refund it to your card.</p></div>
            <div className="rounded-2xl bg-white border border-[#EDE4D3] p-5"><Truck className="w-5 h-5 text-[#8C2220]" /><p className="mt-3 font-semibold">Evri UK delivery</p><p className="text-slate-500 mt-1">Priced by parcel weight{p.weight_kg ? ` (this item ~${p.weight_kg}kg)` : ""}{meta?.free_over != null && ` · free Standard over ${gbp(meta.free_over)}`}</p></div>
          </div>
        </div>
      </div>
      <Reviews p={p} />
    </div>
  );
}
